import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";

const LOGO = "/paras-logo.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-slate-700 bg-slate-900 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-emerald-500";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#070B14] px-6 text-slate-100" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <img src={LOGO} alt="Paras Cashback" className="h-10 w-10 rounded-xl object-contain" />
          <p className="font-heading text-lg font-extrabold">Paras Cashback™</p>
        </div>
        <div className="ledger-card rounded-3xl border border-white/10 p-8 shadow-2xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 font-mono text-xs uppercase tracking-widest text-emerald-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Reset password
          </div>
          {!token ? (
            <p className="text-sm text-rose-300" data-testid="reset-no-token">This reset link is invalid. Please request a new one from the login page.</p>
          ) : done ? (
            <div data-testid="reset-success">
              <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-5 w-5" /><p className="font-heading text-lg font-bold">Password updated</p></div>
              <p className="mt-2 text-sm text-slate-400">You can now sign in with your new password.</p>
              <button data-testid="reset-goto-login" onClick={() => navigate("/login")} className="mt-6 w-full rounded-full bg-emerald-500 py-3 font-semibold text-slate-950 hover:bg-emerald-400">Go to login</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
              <h1 className="font-heading text-2xl font-extrabold">Choose a new password</h1>
              <p className="text-sm text-slate-400">Enter a new password for your account (min 8 characters).</p>
              <div className="relative pt-2"><Lock className="absolute left-3 top-4.5 h-4 w-4 text-slate-500" /><input data-testid="reset-password-input" type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} /></div>
              <div className="relative"><Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" /><input data-testid="reset-confirm-input" type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} /></div>
              <button data-testid="reset-submit" type="submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Update password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
